import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Send } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { WebhookDelivery, WebhookEndpoint } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { DeleteEndpointDialog } from '../components/delete-endpoint-dialog';
import { useDeliveryColumns } from '../components/delivery-columns';
import { deliveryFilters } from '../components/delivery-filters';
import { EndpointActionsMenu, type EndpointAction } from '../components/endpoint-actions-menu';
import { EndpointDialog } from '../components/endpoint-dialog';
import { RotateSecretDialog } from '../components/rotate-secret-dialog';
import { EndpointStatusBadge } from '../components/webhook-badges';
import { useWebhookDeliveries } from '../hooks/use-webhook-deliveries';
import { useWebhookEndpoint } from '../hooks/use-webhook-endpoint';
import {
  useRedeliverWebhook,
  useSendTestWebhook,
  useUpdateWebhook,
} from '../hooks/use-webhook-mutations';

const ROUTE_ID = '/_authed/_active/s/$storeId/settings/webhooks/$endpointId' as const;
const ROUTE_PATH = '/s/$storeId/settings/webhooks/$endpointId' as const;

function Deliveries({ storeId, endpoint }: { storeId: string; endpoint: WebhookEndpoint }) {
  const { t } = useTranslation('webhooks');
  const message = useErrorMessage();
  const queryClient = useQueryClient();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const redeliver = useRedeliverWebhook(storeId);

  const filters = useMemo(
    () => deliveryFilters({ t, queryClient, storeId }),
    [queryClient, storeId, t],
  );
  // The log sits below the endpoint details; the router's default scroll reset would jump to the
  // top on every filter pick and leave the open picker off-screen.
  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({
        search: (prev) => ({ ...prev, ...patch }),
        replace: true,
        resetScroll: false,
      }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange, filters });
  const onRedeliver = useCallback(
    (delivery: WebhookDelivery) =>
      redeliver.mutate(
        { id: endpoint.id, deliveryId: delivery.id },
        { onError: (error) => toast.error(message(error)) },
      ),
    [endpoint.id, message, redeliver],
  );

  const query = useWebhookDeliveries(storeId, endpoint.id, list.params);
  const columns = useDeliveryColumns({
    redeliveringId: redeliver.isPending ? redeliver.variables.deliveryId : null,
    onRedeliver,
  });
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    filters,
    getRowId: (row) => row.id,
    tableId: 'webhook-deliveries',
  });
  const narrowed = Object.keys(list.filters).length > 0 || Boolean(list.cursor);

  return (
    <FormSection
      title={t('deliveries.title')}
      description={t('deliveries.description')}
      tour="settings.webhook.deliveries"
    >
      <DataTable
        table={table}
        search={false}
        loading={query.isPending}
        fetching={query.isFetching}
        error={query.error}
        onRetry={() => void query.refetch()}
        getRowLabel={(row) => row.event}
        resourceName={{
          singular: t('deliveries.resource.one'),
          plural: t('deliveries.resource.other'),
        }}
        emptyState={{ title: narrowed ? t('deliveries.noMatches') : t('deliveries.empty') }}
      />
    </FormSection>
  );
}

function WebhookDetail({ endpoint }: { endpoint: WebhookEndpoint }) {
  const { t } = useTranslation('webhooks');
  const format = useFormat();
  const store = useStore();
  const message = useErrorMessage();
  const navigate = useNavigate();
  const test = useSendTestWebhook(store.id);
  const toggle = useUpdateWebhook(store.id, 'webhooks:toasts.statusChanged');
  const [editing, setEditing] = useState(false);
  const [rotating, setRotating] = useState<WebhookEndpoint | null>(null);
  const [deleting, setDeleting] = useState<WebhookEndpoint | null>(null);
  // The route already requires webhooks.manage; only a closed store makes this read-only.
  const { editable, reason } = useStoreEditable({ permission: [] });

  const onAction = (action: EndpointAction) => {
    switch (action) {
      case 'edit':
        setEditing(true);
        return;
      case 'toggle':
        toggle.mutate(
          {
            id: endpoint.id,
            body: { status: endpoint.status === 'active' ? 'disabled' : 'active' },
          },
          { onError: (error) => toast.error(message(error)) },
        );
        return;
      case 'rotate':
        setRotating(endpoint);
        return;
      case 'delete':
        setDeleting(endpoint);
    }
  };

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-mono text-lg break-all">{endpoint.url}</span>}
        meta={<EndpointStatusBadge status={endpoint.status} />}
        description={endpoint.description ?? undefined}
        backTo={{ to: '/s/$storeId/settings/webhooks', params: { storeId: store.id } }}
        backLabel={t('title')}
        actions={
          editable ? (
            <>
              <EndpointActionsMenu
                endpoint={endpoint}
                onAction={onAction}
                disabled={toggle.isPending}
              />
              <Button
                variant="outline"
                data-tour="settings.webhook.send-test"
                // The backend answers 409 to a test on a disabled endpoint.
                disabled={test.isPending || endpoint.status !== 'active'}
                onClick={() =>
                  test.mutate(endpoint.id, { onError: (error) => toast.error(message(error)) })
                }
              >
                <Send aria-hidden />
                {t('actions.test')}
              </Button>
            </>
          ) : null
        }
      />
      <ReadOnlyBanner reason={reason} />
      <div className="flex flex-col gap-6">
        <FormSection title={t('detail.title')} tour="settings.webhook.details">
          <dl className="grid gap-3 text-sm sm:grid-cols-4">
            <div className="sm:col-span-4">
              <dt className="text-muted-foreground">{t('columns.events')}</dt>
              <dd className="mt-1 flex flex-wrap gap-1">
                {endpoint.events.map((event) => (
                  <Badge key={event} variant="secondary" className="font-mono text-[11px]">
                    {event}
                  </Badge>
                ))}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('columns.lastSuccess')}</dt>
              <dd title={endpoint.lastSuccessAt ? format.utc(endpoint.lastSuccessAt) : undefined}>
                {endpoint.lastSuccessAt ? format.relative(endpoint.lastSuccessAt) : t('never')}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('columns.lastFailure')}</dt>
              <dd title={endpoint.lastFailureAt ? format.utc(endpoint.lastFailureAt) : undefined}>
                {endpoint.lastFailureAt ? format.relative(endpoint.lastFailureAt) : t('never')}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('columns.failures')}</dt>
              <dd className="tabular-nums" data-testid="webhook-failures">
                {endpoint.failureCount}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('detail.created')}</dt>
              <dd title={format.utc(endpoint.createdAt)}>{format.date(endpoint.createdAt)}</dd>
            </div>
          </dl>
          {endpoint.status === 'disabled' ? (
            <p className="text-muted-foreground text-sm">{t('detail.disabledHint')}</p>
          ) : null}
        </FormSection>
        <Deliveries storeId={store.id} endpoint={endpoint} />
      </div>
      {editing ? (
        <EndpointDialog
          storeId={store.id}
          endpoint={endpoint}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(false);
          }}
        />
      ) : null}
      <RotateSecretDialog
        storeId={store.id}
        endpoint={rotating}
        onOpenChange={(open) => {
          if (!open) setRotating(null);
        }}
      />
      <DeleteEndpointDialog
        storeId={store.id}
        endpoint={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        onDeleted={() =>
          void navigate({ to: '/s/$storeId/settings/webhooks', params: { storeId: store.id } })
        }
      />
    </PageContent>
  );
}

function WebhookDetailLoader() {
  const store = useStore();
  const { endpointId } = useParams({ from: ROUTE_ID });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: endpoint } = useWebhookEndpoint(store.id, endpointId);
  return endpoint ? <WebhookDetail endpoint={endpoint} /> : null;
}

export function WebhookDetailPage() {
  return (
    <FeatureErrorBoundary name="webhooks.detail">
      <WebhookDetailLoader />
    </FeatureErrorBoundary>
  );
}
